import React from 'react';

/**
 * Pure presentational payment summary card.
 *
 * Extracted from EventPage.renderPaymentSummary. No state, no derived
 * calculations — `displayPrice` is the already-computed total from the
 * parent, preserving the single source of truth for pricing.
 *
 * Props:
 *   event           — event object (uses .title)
 *   selectedTier    — selected pass tier object
 *   entryQuantity   — integer quantity for the selected tier
 *   selectedAddOns  — { [addonId]: qty }
 *   activeAddOns    — array of active add-on tier objects
 *   discountResult  — { valid, code, discount_amount } or null
 *   displayPrice    — computed total number to render
 */
export default function PaymentSummaryCard({
  event,
  selectedTier,
  entryQuantity,
  selectedAddOns,
  activeAddOns,
  discountResult,
  displayPrice,
}) {
  if (!event || !selectedTier) return null;
  const isComp = selectedTier.is_comp;
  return (
    <div className="mb-5 border border-[#D4AF37]/40 rounded-sm overflow-hidden" data-testid="payment-summary">
      <div className="bg-[#0F0D08] px-4 py-3 border-b border-[#D4AF37]/20">
        <div className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold">Payment Summary</div>
        <div className="text-base font-bold text-[#EAE7E1] mt-0.5">{event.title}</div>
      </div>
      <div className="px-4 py-3 space-y-2 bg-[#050505]">
        <div className="flex justify-between text-sm">
          <span className="text-[#AFAFAF]">{selectedTier.tier_name} x{entryQuantity}</span>
          <span className="text-[#EAE7E1] font-medium">{isComp ? 'FREE' : `$${(selectedTier.price * entryQuantity).toFixed(2)}`}</span>
        </div>
        {selectedTier.attendees_per_pass > 1 && (
          <div className="text-xs text-[#777] pl-2">{selectedTier.attendees_per_pass * entryQuantity} attendees included</div>
        )}
        {Object.keys(selectedAddOns).length > 0 && Object.entries(selectedAddOns).map(([id, qty]) => {
          const ao = activeAddOns.find(a => a.id === id);
          if (!ao) return null;
          return (
            <div key={id} className="flex justify-between text-sm">
              <span className="text-[#AFAFAF]">{ao.tier_name} x{qty}</span>
              <span className="text-[#EAE7E1] font-medium">${(ao.price * qty).toFixed(2)}</span>
            </div>
          );
        })}
        {discountResult?.valid && (
          <div className="flex justify-between text-sm text-[#6EE7B7]">
            <span>Discount ({discountResult.code})</span>
            <span>-${discountResult.discount_amount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-[#1A1A1A]">
          <span className="text-sm font-semibold text-[#EAE7E1]">Total</span>
          <span className="text-2xl font-black text-[#D4AF37]" data-testid="payment-total">${displayPrice.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
